import { useEffect, useMemo } from "react";
import { useFilters, usePagination, useSortBy, useTable } from "react-table";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";

import { dispatch } from "../../../../state/store";
import { setFWUCampaignToEdit } from "../../../../state/ducks";

import { t } from "../../../../services";
import { campaignStatus, strCode } from "../../../../consts";
import { useTableData } from "../../../../hooks";

import {
  FlexContainers,
  Paragraph2,
  SmallButton,
  Table,
  TablePagination,
  WrapperContainer
} from "../../../../components";

import CampaignFiltersTab from "../../../FleetManagement/Filters/CampaignFiltersTab";
import { FWUCampaignTableColumns } from "./FWUCampaignTableColumns";
import { FWUCampaignTableWrapper } from "./styled";
import FilterTabContainer from "../../../FleetManagement/Filters/FilterTabContainer";

const FWUCampaignTable = ({
  fwuCampaignData = [],
  searchValue,
  setSearchValue,
  onCreateFwuCampaign,
  hasMaxActiveFwuCampaigns,
  loading
}) => {
  const { deviceType, devices } = useSelector((store) => store.fleetManagement?.fwuCampaignFilters);

  const error = useSelector((store) => store.fwuCampaigns?.error);

  const data = useTableData({ loading, data: fwuCampaignData });

  const columns = useMemo(() => FWUCampaignTableColumns, []);
  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: {
        sortBy: [
          {
            id: "startDate",
            desc: true
          }
        ]
      }
    },
    useFilters,
    useSortBy,
    usePagination
  );
  const {
    rows, // total rows
    setFilter,
    nextPage, // pagination props
    previousPage,
    canNextPage,
    canPreviousPage,
    gotoPage,
    pageCount,
    setPageSize,
    state: { pageIndex, pageSize }
  } = tableInstance;

  useEffect(() => {
    setFilter("campaignName", searchValue ?? null);
    setFilter("deviceModel", { model: devices, type: deviceType });
  }, [data, devices, deviceType, searchValue]);

  const onEdit = (fwuCampaign) => {
    dispatch(setFWUCampaignToEdit(fwuCampaign));
  };

  return (
    <WrapperContainer width="100%">
      <FilterTabContainer
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        searchPlaceholder={t(strCode.SEARCH_FOR_CAMPAIGN)}
        ControlsComponent={
          <SmallButton
            onClick={onCreateFwuCampaign}
            xShift={-64}
            disabled={loading || hasMaxActiveFwuCampaigns}>
            {t(strCode.NEW_BTN)}
          </SmallButton>
        }
        FilterComponent={CampaignFiltersTab}
      />
      {error ? (
        <WrapperContainer
          Container={FlexContainers.FlexColumnCenter}
          width={"100%"}
          mt={200}
          textAlign={"center"}>
          <Paragraph2>{t(strCode.FWU_CAMPAIGNS_FETCH_ERROR)}</Paragraph2>
        </WrapperContainer>
      ) : (
        fwuCampaignData && (
          <>
            <FWUCampaignTableWrapper>
              <Table
                tableKey="fwu-campaign-table"
                tableInstance={tableInstance}
                onRowClick={!loading ? onEdit : null}
                isLoading={loading}
                searchedText={searchValue}
                hasPagination
                getTooltipMessage={(row) =>
                  row.status === campaignStatus.active || row.status === campaignStatus.scheduled
                    ? t(strCode.EDIT)
                    : t(strCode.VIEW_DETAILS)
                }
              />
            </FWUCampaignTableWrapper>

            <TablePagination
              tableKey="fwu-campaign-table"
              tableSize={rows?.length}
              pageIndex={pageIndex}
              pageSize={pageSize}
              setPageSize={setPageSize}
              canPreviousPage={canPreviousPage}
              canNextPage={canNextPage}
              onPrevPage={previousPage}
              onNextPage={nextPage}
              onFirstPage={() => gotoPage(0)}
              onLastPage={() => gotoPage(pageCount - 1)}
            />
          </>
        )
      )}
    </WrapperContainer>
  );
};

FWUCampaignTable.propTypes = {
  fwuCampaignData: PropTypes.arrayOf(
    PropTypes.shape({
      campaignName: PropTypes.string.isRequired,
      markets: PropTypes.arrayOf(PropTypes.string).isRequired,
      device: PropTypes.string.isRequired,
      versionTo: PropTypes.string.isRequired,
      startDate: PropTypes.string.isRequired,
      deactivationDate: PropTypes.string,
      status: PropTypes.string.isRequired,
      numberOfSucceededThings: PropTypes.number.isRequired
    }).isRequired
  ).isRequired,
  hasMaxActiveFwuCampaigns: PropTypes.bool,
  setSearchValue: PropTypes.func.isRequired,
  onCreateFwuCampaign: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  searchValue: PropTypes.string
};

export default FWUCampaignTable;
